import React, { useState, useCallback } from 'react';
import { FiActivity, FiUsers, FiCalendar, FiZap, FiUserPlus, FiFilter } from 'react-icons/fi';
import { PageHeader, Kpi, Skeleton, Notice, DateRange } from '../Components/UI';
import BarChart from '../Components/UI/BarChart';
import DataTable, { Column } from '../Components/UI/DataTable';
import Pagination from '../Components/UI/Pagination';
import { useCachedQuery } from '../Components/Hooks/useCachedQuery';
import { fetchMoonrock, normalizeSummary, normalizeRetention, normalizeFunnel, normalizeEvents, normalizeList } from '../Utils/MoonrockAPI';
import { formatNumber, formatDateTime } from '../Utils/Format';

/* ------------------------------------------------------------------ */
/*  Types                                                             */
/* ------------------------------------------------------------------ */

type Summary = {
  totalUsers: number;
  dau: number;
  wau: number;
  mau: number;
  eventsToday: number;
  topEvents: { event: string; count: number }[];
  signupsToday: number;
};

type Retention = { day1: number; day7: number; day30: number };

type DailyPoint = { date: string; count: number };

type TopEvent = { event: string; category: string; count: number };

type FunnelStep = { name: string; count: number; rate: number };

type AnalyticsEvent = {
  _id: string;
  createdAt: string;
  userId?: string;
  event: string;
  category?: string;
  page?: string;
};

type EventsResponse = {
  events: AnalyticsEvent[];
  total: number;
  page: number;
  pages: number;
};

/* ------------------------------------------------------------------ */
/*  Helpers                                                           */
/* ------------------------------------------------------------------ */

const pct = (value: number | undefined | null) =>
  value === undefined || value === null || isNaN(value) ? '--' : `${Math.round(value)}%`;

const today = () => {
  const d = new Date();
  return d.toISOString().slice(0, 10);
};

const daysAgo = (n: number) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};

/* ------------------------------------------------------------------ */
/*  Component                                                         */
/* ------------------------------------------------------------------ */

const MoonrockAnalytics: React.FC = () => {
  /* -- Top events date range -- */
  const [topRange, setTopRange] = useState({ from: daysAgo(30), to: today() });

  /* -- Events log state -- */
  const [eventsPage, setEventsPage] = useState(1);
  const [eventsLimit, setEventsLimit] = useState(25);
  const [eventFilter, setEventFilter] = useState('');

  /* -- Data fetching -- */
  const summary = useCachedQuery<Summary>(
    'mr:summary',
    (signal) => fetchMoonrock('/api/analytics/admin/summary', {}, signal).then(normalizeSummary),
    60000,
  );

  const retention = useCachedQuery<Retention>(
    'mr:retention',
    (signal) => fetchMoonrock('/api/analytics/admin/users/retention', {}, signal).then(normalizeRetention),
    60000,
  );

  const activeUsers = useCachedQuery<{ daily: DailyPoint[] }>(
    'mr:active',
    (signal) => fetchMoonrock('/api/analytics/admin/users/active', { days: 30 }, signal).then((raw) => normalizeList(raw, 'daily') as { daily: DailyPoint[] }),
    60000,
  );

  const topEvents = useCachedQuery<{ events: TopEvent[] }>(
    `mr:top:${topRange.from}:${topRange.to}`,
    (signal) => fetchMoonrock('/api/analytics/admin/top-events', { from: topRange.from, to: topRange.to, limit: 20 }, signal).then((raw) => normalizeList(raw, 'events') as { events: TopEvent[] }),
    60000,
  );

  const funnel = useCachedQuery<{ steps: FunnelStep[] }>(
    'mr:funnel',
    (signal) => fetchMoonrock('/api/analytics/admin/funnel', {}, signal).then(normalizeFunnel),
    60000,
  );

  const eventsLog = useCachedQuery<EventsResponse>(
    `mr:events:${eventsPage}:${eventsLimit}:${eventFilter}`,
    (signal) =>
      fetchMoonrock('/api/analytics/admin/events', { page: eventsPage, limit: eventsLimit, event: eventFilter, sort: 'createdAt', dir: 'desc' }, signal).then(normalizeEvents),
    30000,
  );

  const s = summary.data;
  const r = retention.data;
  const funnelMax = funnel.data ? Math.max(1, ...funnel.data.steps.map((step) => step.count)) : 1;

  /* -- Event filter unique list from top events -- */
  const eventNames: string[] = topEvents.data ? topEvents.data.events.map((e) => e.event) : [];

  const handleEventFilter = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setEventFilter(e.target.value);
    setEventsPage(1);
  }, []);

  const handleEventsLimit = useCallback((limit: number) => {
    setEventsLimit(limit);
    setEventsPage(1);
  }, []);

  /* -- Table columns -- */
  const topEventsColumns: Column<TopEvent>[] = [
    { key: 'event', header: 'Event', render: (row) => <strong>{row.event}</strong> },
    { key: 'category', header: 'Category', render: (row) => <span className="badge badge--plain">{row.category || '--'}</span> },
    { key: 'count', header: 'Count', className: 'num', render: (row) => formatNumber(row.count) },
  ];

  const eventsColumns: Column<AnalyticsEvent>[] = [
    { key: 'time', header: 'Time', render: (row) => <span className="mono" style={{ fontSize: '1.25rem' }}>{formatDateTime(row.createdAt)}</span> },
    {
      key: 'userId',
      header: 'User ID',
      render: (row) => (
        <span style={{ maxWidth: '12rem', display: 'inline-block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {row.userId || '--'}
        </span>
      ),
    },
    { key: 'event', header: 'Event', render: (row) => <strong>{row.event}</strong> },
    { key: 'category', header: 'Category', render: (row) => row.category || '--' },
    { key: 'page', header: 'Page', render: (row) => row.page || '--' },
  ];

  return (
    <div className="mr-analytics stack">
      <PageHeader
        title="Moonrock Analytics"
        description="Player metrics and event tracking for the Moonrock language game."
        actions={
          <button className="btn btn--secondary btn--sm" onClick={summary.reload} disabled={summary.loading}>
            <FiActivity /> Refresh
          </button>
        }
      />

      {summary.error && <Notice tone="error">Could not load Moonrock data: {summary.error.message}</Notice>}

      {/* ---------- KPI row ---------- */}
      <div className="grid grid--kpi">
        <Kpi label="DAU" icon={<FiUsers />} tone="navy" value={formatNumber(s && s.dau)} loading={summary.loading && !s} sub="Daily active users" />
        <Kpi label="WAU" icon={<FiUsers />} tone="green" value={formatNumber(s && s.wau)} loading={summary.loading && !s} sub="Weekly active users" />
        <Kpi label="MAU" icon={<FiUsers />} tone="amber" value={formatNumber(s && s.mau)} loading={summary.loading && !s} sub="Monthly active users" />
        <Kpi label="Signups today" icon={<FiUserPlus />} tone="coral" value={formatNumber(s && s.signupsToday)} loading={summary.loading && !s} sub="New players" />
        <Kpi label="Events today" icon={<FiZap />} tone="navy" value={formatNumber(s && s.eventsToday)} loading={summary.loading && !s} sub="Total tracked events" />
      </div>

      {/* ---------- Retention ---------- */}
      <div className="mr-section">
        <h2>Player retention</h2>
        <div className="mr-retention">
          {retention.error ? (
            <Notice tone="error">Could not load retention data.</Notice>
          ) : (
            <>
              <div className="mr-retention__stat">
                <span>Day 1</span>
                <strong>{r ? pct(r.day1) : <Skeleton width="5rem" height="2.4rem" />}</strong>
              </div>
              <div className="mr-retention__stat">
                <span>Day 7</span>
                <strong>{r ? pct(r.day7) : <Skeleton width="5rem" height="2.4rem" />}</strong>
              </div>
              <div className="mr-retention__stat">
                <span>Day 30</span>
                <strong>{r ? pct(r.day30) : <Skeleton width="5rem" height="2.4rem" />}</strong>
              </div>
            </>
          )}
        </div>
      </div>

      {/* ---------- Active users chart ---------- */}
      <div className="panel">
        <div className="panel__head">
          <div>
            <h2>Active users, last 30 days</h2>
            <p>Daily unique players</p>
          </div>
        </div>
        {activeUsers.data && activeUsers.data.daily.length > 0 ? (
          <BarChart data={activeUsers.data.daily} unit="active users" />
        ) : activeUsers.loading ? (
          <Skeleton width="100%" height="16rem" block />
        ) : (
          <p className="muted" style={{ padding: '4rem 0', textAlign: 'center' }}>No active user data yet.</p>
        )}
      </div>

      {/* ---------- Top events + Funnel ---------- */}
      <div className="grid grid--2">
        {/* Top events */}
        <div className="table-card">
          <div className="toolbar">
            <div style={{ flex: 1 }}>
              <h3 style={{ fontSize: '1.6rem', fontWeight: 700 }}>Top events</h3>
              <p className="muted" style={{ fontSize: '1.3rem', marginTop: '0.2rem' }}>Most frequent events in the selected range</p>
            </div>
            <DateRange label="Period" from={topRange.from} to={topRange.to} onChange={setTopRange} />
          </div>
          <DataTable
            columns={topEventsColumns}
            rows={topEvents.data ? topEvents.data.events : []}
            rowKey={(row) => row.event}
            loading={topEvents.loading}
            initialLoading={topEvents.loading && !topEvents.data}
            skeletonRows={8}
            emptyTitle="No events recorded"
          />
        </div>

        {/* Conversion funnel */}
        <div className="mr-section">
          <h2>Conversion funnel</h2>
          {funnel.error ? (
            <Notice tone="error">Could not load funnel data.</Notice>
          ) : funnel.loading && !funnel.data ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.2rem' }}>
              {[0, 1, 2, 3, 4].map((i) => (
                <Skeleton key={i} width="100%" height="4.4rem" block />
              ))}
            </div>
          ) : funnel.data && funnel.data.steps.length > 0 ? (
            <div className="mr-funnel">
              {funnel.data.steps.map((step, index) => (
                <div key={step.name} className="mr-funnel__step" style={{ animationDelay: `${index * 80}ms` }}>
                  <div className="mr-funnel__bar" style={{ width: `${Math.max(8, (step.count / funnelMax) * 100)}%` }} />
                  <div className="mr-funnel__label">
                    <span className="mr-funnel__name">{step.name}</span>
                    <span className="mr-funnel__meta">
                      {formatNumber(step.count)} <span className="muted">({pct(step.rate)})</span>
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="muted" style={{ padding: '4rem 0', textAlign: 'center' }}>No funnel data yet.</p>
          )}
        </div>
      </div>

      {/* ---------- Recent events log ---------- */}
      <div className="table-card">
        <div className="toolbar">
          <div style={{ flex: 1 }}>
            <h3 style={{ fontSize: '1.6rem', fontWeight: 700 }}>Recent events</h3>
            <p className="muted" style={{ fontSize: '1.3rem', marginTop: '0.2rem' }}>Raw event stream, newest first</p>
          </div>
          <div className="field">
            <span><FiFilter style={{ width: '1.2rem', height: '1.2rem', verticalAlign: '-0.15rem' }} /> Event</span>
            <select value={eventFilter} onChange={handleEventFilter}>
              <option value="">All events</option>
              {eventNames.map((name) => (
                <option key={name} value={name}>{name}</option>
              ))}
            </select>
          </div>
        </div>
        <DataTable
          columns={eventsColumns}
          rows={eventsLog.data ? eventsLog.data.events : []}
          rowKey={(row) => row._id}
          loading={eventsLog.loading}
          initialLoading={eventsLog.loading && !eventsLog.data}
          skeletonRows={10}
          emptyTitle="No events recorded"
          emptyBody="Events will appear here once players start using Moonrock."
        />
        {eventsLog.data && eventsLog.data.pages > 0 && (
          <Pagination
            page={eventsLog.data.page}
            totalPages={eventsLog.data.pages}
            total={eventsLog.data.total}
            limit={eventsLimit}
            onPage={setEventsPage}
            onLimit={handleEventsLimit}
            loading={eventsLog.loading}
          />
        )}
      </div>
    </div>
  );
};

export default MoonrockAnalytics;
